import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingLocationType, BookingRecordDetailsCustomer, PaymentOption } from '@navio/nsdk';
import { HardCodedSalesItemId } from 'noddi-types';
import { NoddiBasicCard, NoddiDummyButton } from 'noddi-ui-web';
import { BookingDetailsTabs, customerRoutes, getIsOnlyWindShieldRepairJob } from 'noddi-util';
import { type ReactNode } from 'react';
import { getHardCodedSalesItems } from '../../utils/getHardCodedSalesItem';
import { getRelevantInfo } from '../../utils/getInfoAboutBooking';
import { WheelPickupSigningCard } from '../Booking/WheelPickupSigningCard';
import { InstructionRow } from '../Common/InstructionRow';
import { InviteFriendsTeaser } from '../Referrals/InviteFriendsTeaser';
import { PendingRecallsCard } from './PendingRecallsCard';

export const BookingConfirmationContent = ({
  bookingDetails,
  hideRows = false,
  heading
}: {
  bookingDetails: BookingRecordDetailsCustomer;
  hideRows?: boolean;
  /** Page-owned heading. A plain h1 while the intro is showing, PageHeading once the intro is gone. */
  heading: ReactNode;
}) => {
  // Get all sales item IDs from the order
  const bookingSalesItemIds = bookingDetails.order.orderLines
    .filter((line) => line.salesItemId !== null)
    .map((line) => line.salesItemId!);

  // Filter the hardcoded items that match what's in the booking
  const matchingItems = getHardCodedSalesItems().filter((item) =>
    item.ids.some((id) => bookingSalesItemIds.includes(id))
  );

  const shouldPayForBooking = bookingDetails.order.amountGross.amount > 0;

  const isWindshieldReplacement = getIsOnlyWindShieldRepairJob(bookingDetails);

  // The arrival row and the pickup-logistics info are mobile-only: for a
  // stationary booking the customer drives to the station, so "when we arrive",
  // "you don't need to be home" and "tires within 10 m of the car" are wrong.
  const isStationary = bookingDetails.locationType === BookingLocationType.STATIONARY;
  // Without a visit we only pick up the tires from another tire hotel: there is no arrival to announce.
  const isArrivingAtCustomer = !isStationary && bookingDetails.hasCustomerVisit;

  return (
    <div className='flex w-full flex-col gap-4'>
      {heading}
      {/* Power of Attorney Card */}
      <WheelPickupSigningCard wheelSetPickups={bookingDetails.wheelSetPickups} variant='card' />

      <NoddiBasicCard
        className='flex flex-col gap-3'
        to={customerRoutes.bookingInfo.getPath({
          slug: bookingDetails.slug,
          tab: BookingDetailsTabs.ORDER_DETAILS
        })}
      >
        <p className='text-xl font-semibold text-primary'>
          <Trans>What happens next?</Trans>
        </p>
        {!hideRows && (
          <div className='flex flex-col gap-2'>
            <InstructionRow
              icon='Check'
              title={t`Order confirmation`}
              description={t`A confirmation message will be sent to you via SMS shortly`}
            />
            {isArrivingAtCustomer && (
              <InstructionRow
                icon='Phone'
                title={t`We will keep you updated on when we expect to arrive`}
                description={t`We'll text you the day before with when we expect to arrive, and again when you're next on the list`}
              />
            )}
            {!bookingDetails.hasCustomerVisit && (
              <InstructionRow
                icon='Truck'
                title={t`We pick up your tires`}
                description={t`We'll pick up your tires from your current tire hotel and text you when they're picked up.`}
              />
            )}
            {/* This and the matching items below are two initiatives kind of solving the same problem; the items are salesItem-based info, the other is booking category-based info */}{' '}
            {isArrivingAtCustomer && (
              <InstructionRow
                icon='Info'
                title={t`Important information`}
                subItems={getRelevantInfo({
                  booking: bookingDetails,
                  skipCommonInfo: true,
                  hasCarryingTires: bookingSalesItemIds.includes(HardCodedSalesItemId.HelpToCarryTires)
                })}
              />
            )}
            {/* Dynamically render instruction rows based on matching sales items */}
            {matchingItems.map((item, index) => (
              <InstructionRow key={index} icon={item.icon} title={item.name} description={item.description} />
            ))}
            {isWindshieldReplacement && (
              <InstructionRow
                icon='FileSpreadsheet'
                title={t`Damage report`}
                description={t`You will receive a damage report shortly. Make sure to fill it out before we arrive.`}
              />
            )}
            {shouldPayForBooking && <PaymentInstructionRow paymentOption={bookingDetails.order.paymentOption} />}
          </div>
        )}
        <div className='flex justify-end'>
          <NoddiDummyButton size='small' endIcon='ArrowRight'>
            <Trans>See booking</Trans>
          </NoddiDummyButton>
        </div>
      </NoddiBasicCard>
      <PendingRecallsCard
        userGroupId={bookingDetails.userGroup.id}
        excludedLicensePlate={bookingDetails.bookingItemsCar[0]?.licensePlate.number}
      />
      <InviteFriendsTeaser />
    </div>
  );
};

const PaymentInstructionRow = ({ paymentOption }: { paymentOption: PaymentOption }) => {
  if (paymentOption === PaymentOption.INVOICE) {
    return (
      <InstructionRow
        icon='FileText'
        title={t`Payment`}
        description={t`You will receive an invoice after the job is complete`}
      />
    );
  }

  if (paymentOption === PaymentOption.PAY_AFTER) {
    return (
      <InstructionRow
        icon='CreditCard'
        title={t`Payment`}
        description={t`Once the job is complete, you'll receive a payment link via SMS`}
      />
    );
  }

  return null;
};
